import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  checkSlugAvailability,
  createProject,
  deleteProject,
  fetchMyProjectsForPicker,
  getProject,
  getProjectByHandleAndSlug,
  insertProjectReferenceAtRoot,
  listMyProjects,
  updateProject,
} from '@/api/projects'
import { patchProjectFields } from './cache-transforms'
import type {
  CreateProjectRequest,
  MyProjectsListItem,
  ProjectDetail,
  SlugAvailability,
  UpdateProjectRequest,
} from '@/api/types'

export const PROJECTS_QUERY_KEY = ['projects'] as const

/**
 * Phase 8: signed photo URLs in project responses are valid for ~1 hour. We refresh the parent
 * query just under that TTL so URLs renew before they expire and long-running tabs don't show
 * broken thumbnails.
 */
const SIGNED_URL_STALE_MS = 50 * 60 * 1000

export const useMyProjects = (page: number = 0) =>
  useQuery({
    queryKey: [...PROJECTS_QUERY_KEY, 'list', page],
    queryFn: () => listMyProjects(page, 20),
  })

export const useProjectDetail = (id: string | undefined) =>
  useQuery({
    queryKey: [...PROJECTS_QUERY_KEY, 'detail', id],
    queryFn: () => getProject(id as string),
    enabled: Boolean(id),
    staleTime: SIGNED_URL_STALE_MS,
    retry: (failureCount, error) => {
      const status = (error as { status?: number })?.status
      if (status === 403 || status === 404) return false
      return failureCount < 3
    },
  })

export const useCreateProject = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateProjectRequest) => createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}

interface UpdateProjectCtx {
  previous: ProjectDetail | undefined
}

export const useUpdateProject = (id: string) => {
  const queryClient = useQueryClient()
  const detailKey = [...PROJECTS_QUERY_KEY, 'detail', id]
  return useMutation<unknown, unknown, UpdateProjectRequest, UpdateProjectCtx>({
    mutationFn: (data: UpdateProjectRequest) => updateProject(id, data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<ProjectDetail>(detailKey)
      if (previous) {
        queryClient.setQueryData<ProjectDetail>(detailKey, patchProjectFields(previous, data))
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(detailKey, ctx.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}

export const useDeleteProject = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}

/**
 * Phase 6: resolve `/p/:handle/:slug` to a hydrated `ProjectDetail`. Same retry semantics as
 * `useProjectDetail` — 403 and 404 are terminal so we don't pound the backend.
 */
export const useProjectByHandleSlug = (handle: string | undefined, slug: string | undefined) =>
  useQuery({
    queryKey: [...PROJECTS_QUERY_KEY, 'by-handle-slug', handle, slug],
    queryFn: () => getProjectByHandleAndSlug(handle as string, slug as string),
    enabled: Boolean(handle && slug),
    staleTime: SIGNED_URL_STALE_MS,
    retry: (failureCount, error) => {
      const status = (error as { status?: number })?.status
      if (status === 403 || status === 404) return false
      return failureCount < 3
    },
  })

/**
 * Phase 6: debounced slug availability check (~300ms) for the project header settings panel.
 * Mirrors the shape of Phase 4's `useHandleAvailability`. {@code excludeProjectId} lets the
 * editing project keep its own slug without tripping the uniqueness check.
 */
export const useSlugAvailability = (slug: string | undefined, excludeProjectId?: string) => {
  const [debounced, setDebounced] = useState(slug)
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(slug), 300)
    return () => clearTimeout(handle)
  }, [slug])

  return useQuery<SlugAvailability>({
    queryKey: [...PROJECTS_QUERY_KEY, 'slug-availability', debounced, excludeProjectId],
    queryFn: () => checkSlugAvailability(debounced as string, excludeProjectId),
    enabled: Boolean(debounced && debounced.length >= 3),
    staleTime: 5_000,
  })
}

/** Phase 6: lightweight `(id, title, slug)` listing for the browse-side reference picker. */
export const useMyProjectsForPicker = (enabled: boolean = true) =>
  useQuery<MyProjectsListItem[]>({
    queryKey: [...PROJECTS_QUERY_KEY, 'picker-list'],
    queryFn: fetchMyProjectsForPicker,
    enabled,
    staleTime: 30_000,
  })

/** Phase 6: insert a Project reference at the master's tree root (browse-side flow). */
export const useInsertProjectReferenceAtRoot = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      masterProjectId,
      sourceProjectId,
    }: {
      masterProjectId: string
      sourceProjectId: string
    }) => insertProjectReferenceAtRoot(masterProjectId, sourceProjectId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'detail', vars.masterProjectId],
      })
      queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'by-handle-slug'],
      })
    },
  })
}
